import { createContext, useContext, useRef, useState, type CSSProperties, type ReactNode } from "react";
import gsap from "gsap";
import { Link } from "wouter";
import { Check, Download, Settings2, LayoutTemplate, Send, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WEBSITE_ROUTES } from "@/lib/config";

import { useIsEs } from "@/components/language-provider";

type View = "fillsign" | "d365";
const SwitchCtx = createContext<(to: View) => void>(() => {});

// Panes swap through inline styles owned by GSAP; React only sets these once (constant objects), so a re-render never undoes a swap.
const SHOWN: CSSProperties = {};
const HIDDEN: CSSProperties = { position: "absolute", inset: 0, visibility: "hidden", opacity: 0 };

// Toggle geometry (px): the FillSign mark is a 56px circle; the D365 pill starts 12px under its right edge.
const MARK = 56, PILL_W = 72, PILL_X = MARK - 12;

export function DynamicsSwitch({ pt, children }: { pt: boolean; children: ReactNode }) {
  const es = useIsEs();
  const [view, setView] = useState<View>("fillsign");
  const [touched, setTouched] = useState(false);
  const current = useRef<View>("fillsign");
  const busy = useRef(false);
  const wrap = useRef<HTMLDivElement>(null);
  const fillsign = useRef<HTMLDivElement>(null);
  const d365 = useRef<HTMLDivElement>(null);
  const blob = useRef<HTMLDivElement>(null);

  const go = (to: View) => {
    const w = wrap.current;
    if (busy.current || to === current.current || !w || !blob.current) return;
    busy.current = true; setTouched(true);
    current.current = to; setView(to);
    // The white fill flows out of the FillSign mark into the D365 pill (goo filter merges them on the way), and back.
    const toD365 = to === "d365";
    gsap.timeline()
      .to(blob.current, { scaleX: 1.35, duration: 0.22, ease: "power2.in" }, 0)
      .to(blob.current, { x: toD365 ? PILL_X : 0, width: toD365 ? PILL_W : MARK, duration: 0.55, ease: "power3.inOut" }, 0.08)
      .to(blob.current, { scaleX: 1, duration: 0.5, ease: "elastic.out(1, 0.55)" }, 0.45);
    // Content: quick crossfade with the block height following.
    const inc = (toD365 ? d365 : fillsign).current!;
    const out = (toD365 ? fillsign : d365).current!;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const h0 = w.offsetHeight;
    gsap.set(w, { height: h0 });
    gsap.set(inc, { position: "absolute", inset: 0, visibility: "visible", opacity: 0, zIndex: 2 });
    const h1 = inc.scrollHeight;
    const done = () => {
      gsap.set(inc, { position: "relative", inset: "auto", visibility: "visible", opacity: 1, zIndex: "auto" });
      gsap.set(out, { position: "absolute", inset: 0, visibility: "hidden", opacity: 0 });
      gsap.set(w, { clearProps: "height" });
      busy.current = false;
    };
    if (reduce) return done();
    gsap.timeline({ onComplete: done })
      .to(out, { opacity: 0, duration: 0.22, ease: "power2.out" }, 0)
      .to(w, { height: h1, duration: 0.5, ease: "power3.inOut" }, 0.05)
      .to(inc, { opacity: 1, duration: 0.4, ease: "power2.out" }, 0.18);
  };

  return (
    <SwitchCtx.Provider value={go}>
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <filter id="fs-goo">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="b" />
          <feColorMatrix in="b" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10" result="g" />
          <feComposite in="SourceGraphic" in2="g" operator="atop" />
        </filter>
      </svg>
      <div className="mx-auto max-w-6xl px-5 pt-20 sm:px-8 lg:pt-28">
        <div className="flex items-center gap-4" role="tablist" aria-label="FillSign / Dynamics 365">
          <div className="relative" style={{ height: MARK, width: PILL_X + PILL_W }}>
            {/* liquid layer: the mark's own white disc plus the travelling fill */}
            <div className="absolute inset-0" style={{ filter: "url(#fs-goo)" }} aria-hidden="true">
              <div className="absolute left-0 top-0 rounded-full bg-white" style={{ width: MARK, height: MARK }} />
              <div ref={blob} className="absolute left-0 top-0 origin-left rounded-full bg-white" style={{ width: MARK, height: MARK }} />
            </div>
            <button type="button" role="tab" aria-selected={view === "fillsign"} aria-label="FillSign" onClick={() => go("fillsign")}
              className={`absolute left-0 top-0 z-10 flex items-center justify-center rounded-full border-4 transition-colors duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${view === "fillsign" ? "border-[var(--fs-leaf)]" : "border-[var(--fs-d365-blue)]"}`}
              style={{ width: MARK, height: MARK }}>
              <img src="/favicon.png" alt="" className="h-9 w-9" />
            </button>
            <button type="button" role="tab" aria-selected={view === "d365"} onClick={() => go("d365")}
              className={`absolute top-0 flex items-center justify-end rounded-full pr-4 text-sm font-semibold tracking-tight ring-1 transition-colors duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${view === "d365" ? "text-[var(--fs-d365-blue)] ring-white/0" : "text-white ring-white/30 hover:ring-white/60"} ${!touched && view !== "d365" ? "fs-pulse" : ""}`}
              style={{ left: PILL_X, width: PILL_W, height: MARK }}>
              D365
            </button>
          </div>
          <span className="text-xs text-white/60">
            {view === "d365" ? (pt ? "Toque no logo FillSign para voltar" : es ? "Toque el logo de FillSign para volver" : "Tap the FillSign logo to go back") : (pt ? "Toque no D365 para ver a integração" : es ? "Toque D365 para ver la integración" : "Tap D365 to see the integration")}
          </span>
        </div>
      </div>
      <div ref={wrap} className="relative">
        <div ref={fillsign} style={SHOWN}>{children}</div>
        <div ref={d365} style={HIDDEN}><D365View pt={pt} /></div>
      </div>
    </SwitchCtx.Provider>
  );
}

/** "See the integration" inside the FillSign view: same switch as the D365 button. */
export function D365Button({ pt }: { pt: boolean }) {
  const es = useIsEs();
  const go = useContext(SwitchCtx);
  return (
    <Button onClick={() => go("d365")} className="h-11 w-full rounded-md bg-white px-6 text-[var(--fs-d365-blue)] hover:bg-white/90 sm:w-auto">
      {pt ? "Ver a integração" : es ? "Ver la integración" : "See the integration"}
    </Button>
  );
}

function D365View({ pt }: { pt: boolean }) {
  const es = useIsEs();
  const steps: Array<[LucideIcon, string, string]> = pt
    ? [[Download, "Importe a solução", "Solução gerenciada do FillSign na sua organização (Power Platform → Soluções → Importar). Sem código."],
       [Settings2, "Configure o controle", "Coloque o componente PCF numa seção do formulário (Conta, Oportunidade ou qualquer entidade) e escolha: upload, template ou os dois."],
       [LayoutTemplate, "Mapeie os templates", "No FillSign, monte o contrato com campos do CRM. Cada envio já sai preenchido com o registro aberto."],
       [Send, "Envie do registro", "Assinantes vêm dos contatos e usuários; a IA posiciona; status, reenvio e auditoria ficam no próprio registro."]]
    : es ? [[
    Download,
    "Importe la solución",
    "Solución administrada de FillSign en su organización (Power Platform → Soluciones → Importar). Sin código."
  ], [
    Settings2,
    "Configure el control",
    "Coloque el componente PCF en una sección del formulario (Cuenta, Oportunidad o cualquier entidad) y elija: carga, plantilla o ambos."
  ], [
    LayoutTemplate,
    "Mapee las plantillas",
    "En FillSign, arme el contrato con campos del CRM. Cada envío sale completado con el registro abierto."
  ], [
    Send,
    "Envíe desde el registro",
    "Los firmantes vienen de los contactos y usuarios; la IA posiciona las firmas; el estado, el reenvío y la auditoría quedan en el registro."
  ]] : [[Download, "Import the solution", "FillSign managed solution in your org (Power Platform → Solutions → Import). No code."],
       [Settings2, "Configure the control", "Drop the PCF into a form section (Account, Opportunity or any entity) and choose: upload, template or both."],
       [LayoutTemplate, "Map the templates", "In FillSign, build the contract with CRM fields. Every send goes out filled from the open record."],
       [Send, "Send from the record", "Signers come from contacts and users; AI places signatures; status, resend and audit trail stay on the record."]];
  const bullets = pt
    ? ["Instala em minutos, sem desenvolvedor", "Qualquer entidade, inclusive customizada", "Upload de PDF/Word ou template com campos do CRM", "Assinantes dos contatos e usuários do registro", "IA sugere as posições; você confirma ou ajusta", "Reenvio por e-mail e WhatsApp, auditoria no registro"]
    : es ? [
    "Se instala en minutos, sin desarrollador",
    "Cualquier entidad, incluso personalizada",
    "Carga de PDF/Word o plantilla con campos del CRM",
    "Firmantes de los contactos y usuarios del registro",
    "La IA sugiere las posiciones; usted confirma o ajusta",
    "Reenvío por correo y WhatsApp, auditoría en el registro"
  ] : ["Installs in minutes, no developer", "Any entity, including custom", "PDF/Word upload or template with CRM fields", "Signers from the record's contacts and users", "AI suggests positions; you confirm or adjust", "Resend by email and WhatsApp, audit trail on the record"];
  return (
    <div className="mx-auto max-w-6xl px-5 pb-20 pt-8 sm:px-8 lg:pb-28">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] sm:text-4xl">{pt ? "Dentro do Dynamics 365, em quatro passos" : es ? "Dentro de Dynamics 365, en cuatro pasos" : "Inside Dynamics 365, in four steps"}</h2>
      <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/75">
        {pt ? "Um componente nativo (PCF) instalado na sua organização. Status, PDF assinado e auditoria voltam para o registro." : es ? "Un componente nativo (PCF) instalado en su organización. El estado, el PDF firmado y la auditoría vuelven al registro." : "A native component (PCF) installed in your org. Status, signed PDF and audit trail flow back to the record."}
      </p>
      <ol className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {steps.map(([I, title, desc], i) => (
          <li key={title} className="border-t-2 border-[var(--fs-leaf)] pt-5">
            <span className="text-sm font-medium text-[var(--fs-leaf)]">{i + 1}</span>
            <I className="mt-2 h-6 w-6 text-[var(--fs-leaf)]" />
            <h3 className="mt-3 font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/70">{desc}</p>
          </li>
        ))}
      </ol>
      <ul className="mt-12 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {bullets.map((b) => <li key={b} className="flex items-start gap-2 rounded-md border border-white/15 px-3 py-2 text-white/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fs-leaf)]" />{b}</li>)}
      </ul>
      <div className="mt-10">
        <Link href={WEBSITE_ROUTES.enterpriseContact}><Button className="h-11 rounded-md bg-white px-6 text-[var(--fs-d365-blue)] hover:bg-white/90">{pt ? "Falar com vendas" : es ? "Hablar con ventas" : "Talk to sales"}</Button></Link>
      </div>
    </div>
  );
}
